import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { hsvToRgb, rgbToHex, hexToRgb, rgbToHsv, rgbToHsl, hslToRgb, harmonyColors, clamp } from '@/lib/color';
import { PALETTE_LIBRARY, PALETTE_CATEGORIES, COLOR_TARGETS } from '@/data/paletteLibrary';
import { GRADIENT_PRESETS, GRADIENT_PRESET_CATEGORIES } from '@/data/gradientPresets';
import { storage, genId } from '@/lib/storage';

const HARMONY_KINDS = ['Complementary','Analogous','Triadic','Split Complementary','Tetradic','Monochromatic','Shades','Tints'];
const FILL_MODES = ['Solid Color','Custom Gradient','Preset Gradient','Pattern / Material'];

/* ---------- Interactive SV Panel + Hue slider ---------- */

function ColorPicker({ h, s, v, onChange }) {
  const svRef = useRef(null);
  const hueRef = useRef(null);
  const dragging = useRef(null);

  const setSVFromEvent = useCallback((e) => {
    const el = svRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    const x = clamp(pt.clientX - rect.left, 0, rect.width);
    const y = clamp(pt.clientY - rect.top, 0, rect.height);
    const ns = Math.round((x / rect.width) * 100);
    const nv = Math.round(100 - (y / rect.height) * 100);
    onChange({ s: ns, v: nv });
  }, [onChange]);

  const setHueFromEvent = useCallback((e) => {
    const el = hueRef.current; if (!el) return;
    const rect = el.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    const x = clamp(pt.clientX - rect.left, 0, rect.width);
    onChange({ h: Math.round((x / rect.width) * 359) });
  }, [onChange]);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      if (dragging.current === 'sv') setSVFromEvent(e);
      else if (dragging.current === 'hue') setHueFromEvent(e);
      if (e.cancelable) e.preventDefault();
    };
    const up = () => { dragging.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
  }, [setSVFromEvent, setHueFromEvent]);

  const startSV = (e) => { dragging.current = 'sv'; setSVFromEvent(e); };
  const startHue = (e) => { dragging.current = 'hue'; setHueFromEvent(e); };

  const hueColor = rgbToHex(...hsvToRgb(h, 100, 100));

  return (
    <div className="space-y-3" data-testid="color-picker">
      <div
        ref={svRef}
        data-testid="cp-sv"
        onMouseDown={startSV}
        onTouchStart={startSV}
        className="relative w-full aspect-square rounded-xl border border-zinc-800 overflow-hidden touch-none select-none cursor-crosshair"
        style={{ background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #FFF, ${hueColor})` }}
      >
        <div
          data-testid="cp-puck"
          className="absolute w-5 h-5 -ml-2.5 -mt-2.5 rounded-full border-2 border-white shadow pointer-events-none"
          style={{ left: `${s}%`, top: `${100 - v}%`, background: rgbToHex(...hsvToRgb(h, s, v)), boxShadow: '0 0 0 1px rgba(0,0,0,0.6)' }}
        />
      </div>
      <div
        ref={hueRef}
        data-testid="cp-hue"
        onMouseDown={startHue}
        onTouchStart={startHue}
        className="relative w-full h-6 rounded-full border border-zinc-800 touch-none select-none cursor-pointer"
        style={{ background: 'linear-gradient(to right, #FF0000 0%, #FFFF00 17%, #00FF00 33%, #00FFFF 50%, #0000FF 66%, #FF00FF 83%, #FF0000 100%)' }}
      >
        <div
          data-testid="cp-hue-thumb"
          className="absolute top-1/2 w-4 h-8 -mt-4 -ml-2 rounded-full bg-white border-2 border-zinc-900 pointer-events-none"
          style={{ left: `${(h / 359) * 100}%` }}
        />
      </div>
    </div>
  );
}

/* ---------- Fields (HEX / RGB / HSL / Opacity / Eyedropper) ---------- */

function ColorFields({ h, s, v, opacity, onChange }) {
  const [r, g, b] = hsvToRgb(h, s, v);
  const [hh, ss, ll] = rgbToHsl(r, g, b);
  const hex = rgbToHex(r, g, b);
  const [hexTxt, setHexTxt] = useState(hex);
  useEffect(() => setHexTxt(hex), [hex]);

  const applyHex = (txt) => {
    setHexTxt(txt);
    const rgb = hexToRgb(txt);
    if (rgb) { const [H, S, V] = rgbToHsv(...rgb); onChange({ h: H, s: S, v: V }); }
  };
  const applyRgb = (nr, ng, nb) => {
    const [H, S, V] = rgbToHsv(clamp(+nr || 0, 0, 255), clamp(+ng || 0, 0, 255), clamp(+nb || 0, 0, 255));
    onChange({ h: H, s: S, v: V });
  };
  const applyHsl = (nh, ns, nl) => {
    const [R, G, B] = hslToRgb(clamp(+nh || 0, 0, 360), clamp(+ns || 0, 0, 100), clamp(+nl || 0, 0, 100));
    const [H, S, V] = rgbToHsv(R, G, B);
    onChange({ h: H, s: S, v: V });
  };
  const eyedrop = async () => {
    if (!window.EyeDropper) { toast.error('Eyedropper not supported in this browser'); return; }
    try {
      const ed = new window.EyeDropper();
      const res = await ed.open();
      applyHex(res.sRGBHex);
    } catch {}
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-center">
        <label className="text-[10px] uppercase tracking-widest text-zinc-500 w-10">HEX</label>
        <input data-testid="cp-hex" value={hexTxt} onChange={(e) => applyHex(e.target.value)} className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-1.5 text-sm font-mono" />
        {window.EyeDropper && <button data-testid="cp-eyedropper" onClick={eyedrop} className="chip">Pick</button>}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">R</label><input data-testid="cp-r" type="number" min="0" max="255" value={r} onChange={(e) => applyRgb(e.target.value, g, b)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-mono" /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">G</label><input data-testid="cp-g" type="number" min="0" max="255" value={g} onChange={(e) => applyRgb(r, e.target.value, b)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-mono" /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">B</label><input data-testid="cp-b" type="number" min="0" max="255" value={b} onChange={(e) => applyRgb(r, g, e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-mono" /></div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">H</label><input data-testid="cp-hsl-h" type="number" min="0" max="360" value={hh} onChange={(e) => applyHsl(e.target.value, ss, ll)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-mono" /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">S%</label><input data-testid="cp-hsl-s" type="number" min="0" max="100" value={ss} onChange={(e) => applyHsl(hh, e.target.value, ll)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-mono" /></div>
        <div><label className="text-[10px] uppercase tracking-widest text-zinc-500">L%</label><input data-testid="cp-hsl-l" type="number" min="0" max="100" value={ll} onChange={(e) => applyHsl(hh, ss, e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs font-mono" /></div>
      </div>
      <label className="block text-xs text-zinc-400">Opacity {opacity}%
        <input data-testid="cp-opacity" type="range" min="0" max="100" value={opacity} onChange={(e) => onChange({ opacity: +e.target.value })} className="w-full accent-pink-500" />
      </label>
    </div>
  );
}

/* ---------- Palette Chips (editable) ---------- */

function PaletteChips({ swatches, selectedIdx, onSelect, onEdit, onRemove, onDuplicate, onReorder, onAdd, current }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2"><div className="eyebrow">Palette · {swatches.length}/8</div>
        <div className="flex gap-1">
          <button data-testid="pl-add" onClick={() => onAdd(current)} disabled={swatches.length >= 8} className="chip disabled:opacity-40">+ Add Color</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {swatches.map((c, i) => (
          <div key={i} className={`rounded-lg border ${selectedIdx === i ? 'border-pink-500' : 'border-zinc-700'} bg-zinc-950 px-2 py-1.5 flex items-center gap-2`} data-testid={`pl-chip-${i}`}>
            <button onClick={() => onSelect(i)} className="w-5 h-5 rounded border border-white/20" style={{ background: c }} aria-label={`Select swatch ${i + 1}`} />
            <span className="font-mono text-[11px] text-zinc-200">{c}</span>
            <button data-testid={`pl-dup-${i}`} onClick={() => onDuplicate(i)} className="text-[10px] text-zinc-400 hover:text-white">Dup</button>
            <button data-testid={`pl-left-${i}`} onClick={() => onReorder(i, i - 1)} className="text-[10px] text-zinc-400 hover:text-white">◀</button>
            <button data-testid={`pl-right-${i}`} onClick={() => onReorder(i, i + 1)} className="text-[10px] text-zinc-400 hover:text-white">▶</button>
            <button data-testid={`pl-rm-${i}`} onClick={() => onRemove(i)} className="text-[10px] text-pink-400 hover:text-pink-300">✕</button>
          </div>
        ))}
        {swatches.length === 0 && <div className="text-xs text-zinc-500">Empty. Pick a color and Add.</div>}
      </div>
    </div>
  );
}

/* ---------- Preset Library ---------- */

function PresetLibrary({ onLoad, onAddSwatches }) {
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const filtered = useMemo(() => PALETTE_LIBRARY.filter(p => (cat === 'All' || p.cat === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase()) || p.cat.toLowerCase().includes(q.toLowerCase()))), [cat, q]);
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-2">
        <input data-testid="pp-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search 75+ curated palettes…" className="flex-1 min-w-[180px] bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs" />
        <select data-testid="pp-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs">
          <option value="All">All Categories</option>
          {PALETTE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[320px] overflow-y-auto scrollbar-thin pr-1">
        {filtered.map(p => (
          <div key={p.id} className="rounded-lg chrome-border bg-zinc-950 p-3 space-y-2" data-testid={`pp-card-${p.id}`}>
            <div className="flex gap-1">{p.colors.map((c, i) => <span key={i} className="flex-1 h-6 rounded" style={{ background: c }} />)}</div>
            <div className="flex items-center justify-between">
              <div><div className="text-xs text-zinc-200">{p.name}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.cat}</div></div>
              <div className="flex gap-1">
                <button data-testid={`pp-load-${p.id}`} onClick={() => onLoad(p)} className="chip">Load</button>
                <button data-testid={`pp-add-${p.id}`} onClick={() => onAddSwatches(p)} className="chip">Add</button>
              </div>
            </div>
            <div className="flex gap-1 flex-wrap">{p.colors.map((c, i) => <span key={i} className="font-mono text-[10px] text-zinc-400">{c}</span>)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Gradient Editor ---------- */

const GRAD_TYPES = ['Linear','Radial','Conic','Diamond','Reflected'];

export function GradientEditor({ gradient, onChange, current }) {
  const barRef = useRef(null);
  const dragging = useRef(null);
  const stateRef = useRef({ gradient, onChange });
  useEffect(() => { stateRef.current = { gradient, onChange }; });
  const [sel, setSel] = useState(0);
  const stops = gradient.stops || [];
  const sorted = [...stops].sort((a, b) => a.pos - b.pos);

  const cssBg = useMemo(() => {
    if (stops.length < 2) return '#111';
    const parts = sorted.map(s => `${s.color}${s.opacity != null ? Math.round(s.opacity * 2.55).toString(16).padStart(2, '0') : ''} ${Math.round(s.pos)}%`);
    return `linear-gradient(90deg, ${parts.join(', ')})`;
  }, [stops, sorted]);

  const setPos = (idx, pct) => {
    const { gradient: g, onChange: cb } = stateRef.current;
    const st = g.stops || [];
    const next = st.map((s, i) => i === idx ? { ...s, pos: clamp(Math.round(pct), 0, 100) } : s);
    cb({ ...g, stops: next });
  };
  const onBarPointer = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const pt = e.touches ? e.touches[0] : e;
    const pct = ((pt.clientX - rect.left) / rect.width) * 100;
    if (dragging.current != null) setPos(dragging.current, pct);
  };
  useEffect(() => {
    const move = (e) => { if (dragging.current != null) { onBarPointer(e); if (e.cancelable) e.preventDefault(); } };
    const up = () => { dragging.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('mouseup', up);
    window.addEventListener('touchend', up);
    return () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('touchmove', move);
      window.removeEventListener('mouseup', up);
      window.removeEventListener('touchend', up);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addStop = () => {
    if (stops.length >= 8) return;
    const pos = stops.length === 0 ? 0 : stops.length === 1 ? 100 : 50;
    onChange({ ...gradient, stops: [...stops, { color: current || '#FF107A', pos, opacity: 100 }] });
  };
  const removeStop = (i) => { if (stops.length <= 2) return; onChange({ ...gradient, stops: stops.filter((_, x) => x !== i) }); };
  const duplicateStop = (i) => { if (stops.length >= 8) return; const s = stops[i]; onChange({ ...gradient, stops: [...stops, { ...s, pos: clamp(s.pos + 5, 0, 100) }] }); };
  const reverse = () => onChange({ ...gradient, stops: stops.map(s => ({ ...s, pos: 100 - s.pos })) });
  const spaceEvenly = () => onChange({ ...gradient, stops: stops.map((s, i, a) => ({ ...s, pos: Math.round((i / Math.max(a.length - 1, 1)) * 100) })) });
  const editSelectedColor = (color) => {
    const next = stops.map((s, i) => i === sel ? { ...s, color } : s);
    onChange({ ...gradient, stops: next });
  };
  const editSelectedOpacity = (op) => {
    const next = stops.map((s, i) => i === sel ? { ...s, opacity: op } : s);
    onChange({ ...gradient, stops: next });
  };
  const editSelectedPos = (pos) => setPos(sel, pos);

  return (
    <div className="space-y-3" data-testid="gradient-editor">
      <div className="flex flex-wrap gap-2 items-center">
        <select data-testid="ge-type" value={gradient.type || 'Linear'} onChange={(e) => onChange({ ...gradient, type: e.target.value })} className="bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs">
          {GRAD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <label className="text-xs text-zinc-400 flex items-center gap-2">Angle
          <input data-testid="ge-angle" type="number" min="0" max="360" value={gradient.angle ?? 90} onChange={(e) => onChange({ ...gradient, angle: clamp(+e.target.value || 0, 0, 360) })} className="w-20 bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono" />
        </label>
        <button data-testid="ge-add" onClick={addStop} className="chip">+ Stop</button>
        <button data-testid="ge-dup" onClick={() => duplicateStop(sel)} className="chip">Duplicate</button>
        <button data-testid="ge-reverse" onClick={reverse} className="chip">Reverse</button>
        <button data-testid="ge-even" onClick={spaceEvenly} className="chip">Space Evenly</button>
      </div>
      <div
        ref={barRef}
        data-testid="ge-bar"
        className="relative h-10 rounded-lg border border-zinc-700"
        style={{ background: cssBg }}
      >
        {stops.map((s, i) => (
          <button
            key={i}
            data-testid={`ge-stop-${i}`}
            onMouseDown={(e) => { dragging.current = i; setSel(i); e.stopPropagation(); }}
            onTouchStart={(e) => { dragging.current = i; setSel(i); }}
            className={`absolute top-full mt-1 w-4 h-4 -ml-2 rounded-full border-2 ${sel === i ? 'border-pink-500 ring-2 ring-pink-500/40' : 'border-white'} touch-none cursor-grab active:cursor-grabbing`}
            style={{ left: `${s.pos}%`, background: s.color }}
            aria-label={`Stop ${i + 1}`}
          />
        ))}
      </div>
      {stops.length > 0 && (
        <div className="pt-6 space-y-2 border-t border-white/5">
          <div className="flex items-center justify-between text-xs text-zinc-400">
            <span>Selected Stop {sel + 1} · <span className="font-mono text-zinc-200">{stops[sel]?.color}</span></span>
            <button data-testid="ge-rm" onClick={() => removeStop(sel)} disabled={stops.length <= 2} className="text-pink-400 disabled:opacity-40">Remove</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Position %
              <input data-testid="ge-pos" type="number" min="0" max="100" value={stops[sel]?.pos ?? 0} onChange={(e) => editSelectedPos(+e.target.value || 0)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono" />
            </label>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">HEX
              <input data-testid="ge-hex" value={stops[sel]?.color || ''} onChange={(e) => editSelectedColor(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono" />
            </label>
            <label className="text-[10px] uppercase tracking-widest text-zinc-500">Opacity %
              <input data-testid="ge-op" type="number" min="0" max="100" value={stops[sel]?.opacity ?? 100} onChange={(e) => editSelectedOpacity(+e.target.value || 0)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1 text-xs font-mono" />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Preset Gradient Library ---------- */

function PresetGradientLibrary({ onLoad }) {
  const [cat, setCat] = useState('All');
  const [q, setQ] = useState('');
  const filtered = useMemo(() => GRADIENT_PRESETS.filter(p => (cat === 'All' || p.cat === cat) && (!q || p.name.toLowerCase().includes(q.toLowerCase()) || p.cat.toLowerCase().includes(q.toLowerCase()))), [cat, q]);
  const cssBg = (g) => {
    const parts = g.stops.map(s => `${s.color} ${Math.round(s.pos)}%`);
    return `linear-gradient(90deg, ${parts.join(', ')})`;
  };
  return (
    <div data-testid="preset-gradient-library">
      <div className="flex flex-wrap gap-2 mb-2">
        <input data-testid="pg-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${GRADIENT_PRESETS.length} preset gradients…`} className="flex-1 min-w-[180px] bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs" />
        <select data-testid="pg-cat" value={cat} onChange={(e) => setCat(e.target.value)} className="bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs">
          <option value="All">All Categories</option>
          {GRADIENT_PRESET_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[280px] overflow-y-auto scrollbar-thin pr-1">
        {filtered.map(p => (
          <button key={p.id} data-testid={`pg-load-${p.id}`} onClick={() => onLoad(p)} className="rounded-lg chrome-border chrome-hover bg-zinc-950 p-3 space-y-2 text-left">
            <div className="h-8 rounded" style={{ background: cssBg(p) }} />
            <div><div className="text-xs text-zinc-200">{p.name}</div><div className="text-[10px] uppercase tracking-widest text-zinc-500">{p.cat} · {p.stops.length} stops</div></div>
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Main ColorLab ---------- */

export default function ColorLab({
  swatches = [], onSwatches,
  gradient = { type: 'Linear', angle: 90, stops: [] }, onGradient,
  target = 'Main Text Fill', onTarget,
  fillMode = 'Solid Color', onFillMode,
  solidColor = '#FF107A', onSolidColor,
  solidOpacity = 100, onSolidOpacity,
  targetFills = {}, onTargetFills,
}) {
  const [h, setH] = useState(328);
  const [s, setS] = useState(90);
  const [v, setV] = useState(85);
  const [opacity, setOpacity] = useState(100);
  const [selIdx, setSelIdx] = useState(-1);
  const [customPalettes, setCustomPalettes] = useState(() => storage.listCustomPalettes());
  const [customGradients, setCustomGradients] = useState(() => storage.listCustomGradients());
  const [saveName, setSaveName] = useState('');

  const currentHex = rgbToHex(...hsvToRgb(h, s, v));
  const setColor = (patch) => {
    if (patch.h != null) setH(patch.h);
    if (patch.s != null) setS(patch.s);
    if (patch.v != null) setV(patch.v);
    if (patch.opacity != null) setOpacity(patch.opacity);
  };

  const addSwatch = (c) => { if ((swatches || []).length >= 8) return; onSwatches([...(swatches || []), c]); };
  const removeSwatch = (i) => onSwatches(swatches.filter((_, x) => x !== i));
  const duplicateSwatch = (i) => { if (swatches.length >= 8) return; onSwatches([...swatches.slice(0, i + 1), swatches[i], ...swatches.slice(i + 1)]); };
  const reorderSwatch = (i, j) => {
    if (j < 0 || j >= swatches.length) return;
    const next = [...swatches]; const [it] = next.splice(i, 1); next.splice(j, 0, it); onSwatches(next);
  };
  const selectSwatch = (i) => {
    setSelIdx(i);
    const rgb = hexToRgb(swatches[i]);
    if (rgb) { const [H, S, V] = rgbToHsv(...rgb); setH(H); setS(S); setV(V); }
  };
  const editCurrentSwatch = (hex) => {
    if (selIdx < 0) return;
    const next = swatches.map((c, i) => (i === selIdx ? hex : c));
    onSwatches(next);
  };
  useEffect(() => { if (selIdx >= 0 && selIdx < swatches.length) editCurrentSwatch(currentHex); /* eslint-disable-line */ }, [h, s, v]);

  const loadHarmony = (kind) => {
    const cs = harmonyColors(h, s, v, kind);
    onSwatches(cs);
    toast.success(`${kind} loaded`);
  };
  const suggestN = (n) => {
    let cs;
    if (n === 3) cs = harmonyColors(h, s, v, 'Analogous');
    else if (n === 4) cs = harmonyColors(h, s, v, 'Tetradic');
    else {
      // 5-color pentad: analogous + complement pair
      const mono = harmonyColors(h, s, v, 'Analogous');
      const comp = harmonyColors((h + 180) % 360, s, v, 'Analogous').slice(0, 2);
      cs = [...mono, ...comp];
    }
    onSwatches(cs.slice(0, n));
  };
  const usePaletteAsGradient = () => {
    if (!swatches.length) return;
    const stops = swatches.map((c, i, a) => ({ color: c, pos: Math.round((i / Math.max(a.length - 1, 1)) * 100), opacity: 100 }));
    onGradient({ ...gradient, stops });
    toast.success('Palette copied into gradient');
  };
  const loadPreset = (p) => { onSwatches([...p.colors]); toast.success(`Loaded ${p.name}`); };
  const addPresetSwatches = (p) => {
    const room = 8 - swatches.length;
    if (room <= 0) return toast.error('Palette full (8)');
    onSwatches([...swatches, ...p.colors.slice(0, room)]);
  };

  // Apply Solid Color to current target
  const applySolid = () => {
    if (onSolidColor) onSolidColor(currentHex);
    if (onSolidOpacity) onSolidOpacity(opacity);
    if (onFillMode) onFillMode('Solid Color');
    if (onTargetFills) onTargetFills({ ...targetFills, [target]: { mode: 'Solid Color', color: currentHex, opacity } });
    toast.success(`Solid color applied to ${target}`);
  };
  const applyGradientToTarget = () => {
    if (!onTargetFills) return;
    if (!(gradient.stops || []).length) return toast.error('No gradient stops to apply');
    onTargetFills({ ...targetFills, [target]: { mode: fillMode === 'Preset Gradient' ? 'Preset Gradient' : 'Custom Gradient', gradient } });
    toast.success(`Gradient applied to ${target}`);
  };
  const clearTargetFill = () => {
    if (!onTargetFills) return;
    const next = { ...targetFills }; delete next[target];
    onTargetFills(next);
    toast.success(`${target} fill cleared`);
  };

  // Save / load / delete custom palettes
  const saveCurrentPalette = () => {
    const name = (saveName || 'Untitled Palette').trim();
    if (!swatches.length) return toast.error('Palette is empty');
    const pal = { id: genId(), name, colors: [...swatches], savedAt: new Date().toISOString() };
    storage.saveCustomPalette(pal);
    setCustomPalettes(storage.listCustomPalettes());
    setSaveName('');
    toast.success(`Saved palette "${name}"`);
  };
  const loadCustomPalette = (p) => { onSwatches([...p.colors]); toast.success(`Loaded ${p.name}`); };
  const deleteCustomPalette = (id) => { storage.deleteCustomPalette(id); setCustomPalettes(storage.listCustomPalettes()); };

  // Save / load / delete custom gradients
  const saveCurrentGradient = () => {
    if (!gradient.stops || gradient.stops.length < 2) return toast.error('Add at least 2 stops before saving');
    const name = (saveName || 'Untitled Gradient').trim();
    const g = { id: genId(), name, type: gradient.type || 'Linear', angle: gradient.angle ?? 90, stops: gradient.stops.map(s => ({ ...s })), savedAt: new Date().toISOString() };
    storage.saveCustomGradient(g);
    setCustomGradients(storage.listCustomGradients());
    setSaveName('');
    toast.success(`Saved gradient "${name}"`);
  };
  const loadCustomGradient = (g) => { onGradient({ type: g.type, angle: g.angle, stops: g.stops.map(s => ({ ...s })) }); toast.success(`Loaded ${g.name}`); };
  const deleteCustomGradient = (id) => { storage.deleteCustomGradient(id); setCustomGradients(storage.listCustomGradients()); };
  const loadPresetGradient = (p) => {
    onGradient({ type: p.type, angle: p.angle, stops: p.stops.map(s => ({ ...s })) });
    if (onFillMode) onFillMode('Preset Gradient');
    toast.success(`Loaded ${p.name}`);
  };

  const gradientCss = useMemo(() => {
    if (!(gradient.stops || []).length) return '#111';
    const parts = [...gradient.stops].sort((a,b)=>a.pos-b.pos).map(s => `${s.color} ${Math.round(s.pos)}%`);
    return `linear-gradient(90deg, ${parts.join(', ')})`;
  }, [gradient]);

  return (
    <div className="space-y-5" data-testid="color-lab">
      {/* Fill Mode */}
      <div className="rounded-xl chrome-border bg-zinc-950/60 p-3 space-y-2">
        <div className="eyebrow">Fill Mode · what should this fill look like?</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {FILL_MODES.map(m => (
            <button
              key={m}
              data-testid={`fill-mode-${m.toLowerCase().replace(/[^a-z]+/g,'-')}`}
              onClick={() => onFillMode && onFillMode(m)}
              className={`rounded-lg px-3 py-2 text-xs text-left transition-colors ${fillMode === m ? 'bg-pink-500/20 border border-pink-500 text-white' : 'chrome-border chrome-hover bg-zinc-950 text-zinc-300'}`}
            >
              {m}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-zinc-500">A palette is not automatically a gradient. Solid color, custom gradient, preset gradient and pattern/material are separate. Whatever you pick here is what the prompt will describe.</p>
      </div>

      {/* Fill Target */}
      <div>
        <div className="eyebrow mb-2">Apply This Fill To (Target)</div>
        <select data-testid="cl-target" value={target} onChange={(e) => onTarget(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm">
          {COLOR_TARGETS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        {targetFills[target] && (
          <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-300" data-testid="cl-target-fill">
            <span className="chrome-border rounded-full px-2 py-0.5">Current: {targetFills[target].mode}</span>
            {targetFills[target].color && <span className="inline-flex items-center gap-1"><span className="w-4 h-4 rounded border border-white/20" style={{ background: targetFills[target].color }} />{targetFills[target].color}</span>}
            <button data-testid="cl-target-clear" onClick={clearTargetFill} className="chip">Clear</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
        <ColorPicker h={h} s={s} v={v} onChange={setColor} />
        <div className="space-y-3">
          <ColorFields h={h} s={s} v={v} opacity={opacity} onChange={setColor} />
          <div className="flex flex-wrap gap-2 pt-1">
            <button data-testid="apply-solid-color" onClick={applySolid} className="btn-magenta rounded-full px-4 py-2 text-xs uppercase tracking-widest">Apply Solid Color</button>
            <button data-testid="pl-add-current" onClick={() => addSwatch(currentHex)} className="chip">+ Add To Palette</button>
            {selIdx >= 0 && <button data-testid="pl-apply-current" onClick={() => editCurrentSwatch(currentHex)} className="chip">Update Selected Swatch</button>}
          </div>
        </div>
      </div>

      <PaletteChips
        swatches={swatches}
        selectedIdx={selIdx}
        current={currentHex}
        onSelect={selectSwatch}
        onEdit={editCurrentSwatch}
        onRemove={removeSwatch}
        onDuplicate={duplicateSwatch}
        onReorder={reorderSwatch}
        onAdd={addSwatch}
      />

      {/* Save/Load custom palettes + gradients */}
      <div className="rounded-xl chrome-border bg-zinc-950/60 p-3 space-y-3">
        <div className="eyebrow">Save Custom Palette / Gradient</div>
        <div className="flex flex-wrap gap-2">
          <input data-testid="cp-save-name" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Name this palette or gradient…" className="flex-1 min-w-[200px] bg-zinc-950 border border-zinc-800 rounded-full px-3 py-1.5 text-xs" />
          <button data-testid="cp-save-palette" onClick={saveCurrentPalette} className="chip">Save Palette</button>
          <button data-testid="cp-save-gradient" onClick={saveCurrentGradient} className="chip">Save Gradient</button>
        </div>
        {customPalettes.length > 0 && (
          <div>
            <div className="eyebrow mb-1">Saved Palettes · {customPalettes.length}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto scrollbar-thin pr-1">
              {customPalettes.map(p => (
                <div key={p.id} className="rounded-lg chrome-border bg-zinc-950 p-2" data-testid={`cp-p-${p.id}`}>
                  <div className="flex gap-1 mb-1">{p.colors.map((c, i) => <span key={i} className="flex-1 h-4 rounded" style={{ background: c }} />)}</div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-zinc-200 truncate">{p.name}</div>
                    <div className="flex gap-1">
                      <button data-testid={`cp-pload-${p.id}`} onClick={() => loadCustomPalette(p)} className="chip">Load</button>
                      <button data-testid={`cp-pdel-${p.id}`} onClick={() => deleteCustomPalette(p.id)} className="text-[10px] text-pink-400">✕</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {customGradients.length > 0 && (
          <div>
            <div className="eyebrow mb-1">Saved Gradients · {customGradients.length}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[180px] overflow-y-auto scrollbar-thin pr-1">
              {customGradients.map(g => (
                <div key={g.id} className="rounded-lg chrome-border bg-zinc-950 p-2" data-testid={`cp-g-${g.id}`}>
                  <div className="h-6 rounded mb-1" style={{ background: `linear-gradient(90deg, ${[...g.stops].sort((a,b)=>a.pos-b.pos).map(s => `${s.color} ${Math.round(s.pos)}%`).join(', ')})` }} />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-zinc-200 truncate">{g.name}</div>
                    <div className="flex gap-1">
                      <button data-testid={`cp-gload-${g.id}`} onClick={() => loadCustomGradient(g)} className="chip">Load</button>
                      <button data-testid={`cp-gdel-${g.id}`} onClick={() => deleteCustomGradient(g.id)} className="text-[10px] text-pink-400">✕</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2"><div className="eyebrow">Harmony Suggestions</div>
          <div className="flex gap-1">
            <button data-testid="cl-sug-3" onClick={() => suggestN(3)} className="chip">Suggest 3</button>
            <button data-testid="cl-sug-4" onClick={() => suggestN(4)} className="chip">Suggest 4</button>
            <button data-testid="cl-sug-5" onClick={() => suggestN(5)} className="chip">Suggest 5</button>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {HARMONY_KINDS.map(k => {
            const cs = harmonyColors(h, s, v, k);
            return (
              <button key={k} data-testid={`cl-harm-${k.toLowerCase().replace(/\s/g, '-')}`} onClick={() => loadHarmony(k)} className="chrome-border chrome-hover rounded-lg p-2 text-left">
                <div className="flex gap-1 mb-1">{cs.slice(0, 5).map((c, i) => <span key={i} className="flex-1 h-5 rounded" style={{ background: c }} />)}</div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-300">{k}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="eyebrow">Curated Palette Library · {PALETTE_LIBRARY.length}</div>
        </div>
        <PresetLibrary onLoad={loadPreset} onAddSwatches={addPresetSwatches} />
      </div>

      <div className="rounded-xl chrome-border bg-zinc-950/60 p-3 space-y-3">
        <div className="flex items-center justify-between">
          <div className="eyebrow">Preset Gradient Library · {GRADIENT_PRESETS.length}</div>
        </div>
        <PresetGradientLibrary onLoad={loadPresetGradient} />
      </div>

      <div className="rounded-xl chrome-border bg-zinc-950/60 p-3 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="eyebrow">Custom Gradient Editor</div>
          <div className="flex gap-1">
            <button data-testid="ge-from-palette" onClick={usePaletteAsGradient} className="chip">Use Palette As Gradient</button>
            <button data-testid="apply-gradient-target" onClick={applyGradientToTarget} className="chip">Apply Gradient to {target}</button>
          </div>
        </div>
        {/* Live gradient preview */}
        <div data-testid="cl-gradient-preview" className="h-8 rounded border border-zinc-800" style={{ background: gradientCss }} />
        <GradientEditor gradient={gradient} onChange={onGradient} current={currentHex} />
      </div>
    </div>
  );
}
